

import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { IconQrcode, IconQrcodeOff, IconLoader2 } from "@tabler/icons-react";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useTranslations } from "next-intl";
import { runPartyCheckIn } from "@/lib/guests/checkInParty";
import { FormInput } from "@/components/Inputs/FormInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { useToast } from "@/components/Toast/Toast";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateAfterCheckIn } from "@/lib/guests/invalidateAfterCheckIn";
import { checkInFailureText } from "@/lib/guests/checkInFailureText";
import { loadPartyProbe, opensPartySheet } from "@/lib/guests/party";
import { PCheckInParty } from "@/components/PCheckInParty/PCheckInParty";
import type { Guest } from "@/types/Guest";

type TCCCheckInLogCTAProps = {
  /** `checkedIn: null` — the status is unknown (nulled for this role): no button. */
  value: { guestId: string; checkedIn: boolean | null; eventId: string; action?: string };
  eventId: string;
};

export const TCCCheckInLogCTA: React.FC<TCCCheckInLogCTAProps> = React.memo(
  function TCCCheckInLogCTA({ value, eventId }) {
    // Optional variant: TableCellContent is reused by render sites outside
    // the event scope (no CapabilitiesProvider) — fail closed (hidden) there.
    const { can, fieldAllowlist } = useCapabilitiesOptional();
    const t = useTranslations("guestDetails");
    const tPicker = useTranslations("checkInPicker");
    const tCheckIn = useTranslations("checkIn");
    const tParty = useTranslations("checkInParty");
    const tErrors = useTranslations("errors");
    const { showToast } = useToast();
    const { blockIfPreviewing } = usePreviewWriteGuard();
    const queryClient = useQueryClient();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [deviceName, setDeviceName] = useState("Admin Suite");
    /**
     * A ref as well as state (`TCCAction.tsx:64-66`): two clicks inside one tick
     * read the same state value, so state alone cannot stop a double submit —
     * and neither can the `disabled` attribute it drives.
       */
    const submitRef = useRef(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
    /**
     * The guest behind this log row, read when the popover opens — a log row is
     * an EVENT and carries no `numCompanions`, so "does this guest have anybody
     * with them" cannot be answered from `value`. Loaded while the operator is
     * looking at the device-name field, so by Confirm it is normally there.
     * `null` = read failed or the guest is gone; the button then does exactly
     * what it did before the sheet existed.
     */
    const [partyProbe, setPartyProbe] = useState<Guest | null | undefined>(undefined);
    const [partyOpen, setPartyOpen] = useState(false);

    const shouldCheckIn = !value.checkedIn;

    const updatePosition = useCallback(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPopoverPos({
          top: rect.bottom + 4,
          left: rect.right - 240,
        });
      }
    }, []);

    useEffect(() => {
      if (!open) return;
      updatePosition();
    }, [open, updatePosition]);

    // Probe once per opening. Re-reading on every render would fight the
    // operator typing a device name, and re-reading on every OPEN is what keeps
    // a guest checked in at the door a minute ago from being offered as solo.
    useEffect(() => {
      if (!open) return;
      let cancelled = false;
      setPartyProbe(undefined);
      loadPartyProbe(eventId, value.guestId)
        .then((row) => {
          if (!cancelled) setPartyProbe(row);
        })
        .catch(() => {
          if (!cancelled) setPartyProbe(null);
        });
      return () => {
        cancelled = true;
      };
    }, [open, eventId, value.guestId]);

    useEffect(() => {
      if (!open) return;
      const handleClickOutside = (e: MouseEvent) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(e.target as Node)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    // Capability gate (audit N12) — replaces the legacy role-floor check.
    if (!can("guests:check-in")) return null;
    // Field gate, the same rule as the guest list's TCCCheckInOut: the label
    // below IS the guest's status ("Check in" / "Check out"). A role that may
    // not read `checkedIn` would learn every guest's status from it — no field,
    // no button (round-2 audit MED-A). `Array.isArray`: the optional fallback
    // and older hosts carry no allowlist at all.
    if (Array.isArray(fieldAllowlist) && !fieldAllowlist.includes("checkedIn")) return null;
    // No status on the row (main-backend nulls it for such a role): the
    // direction is unknown, and a guessed "Check in" would be a wrong write.
    if (value.checkedIn === null) return null;

    // Only show CTA for CHECK_IN/CHECK_OUT actions
    const action = value.action;
    if (action && action !== "CHECK_IN" && action !== "CHECK_OUT") return null;

    const handleConfirm = async () => {
      // Read-only preview: the chrome shows this button with the previewed
      // role's FULL capabilities while the token is masked read-only. Without
      // this the write fires, the mask 403s, and `classify` answers
      // `notAllowed` — which this chain now renders as "You don't have
      // permission to do this". The role DOES; the preview is the read-only
      // half. That misreading is exactly what the guard exists to prevent.
      if (blockIfPreviewing()) return;
      if (submitRef.current) return;
      // Companions come along — but only if the operator says so, and only
      // where they exist. `partyProbe` is `undefined` while the read that
      // decides that is still in flight; waiting is what keeps this
      // deterministic, and it is one small request started when the popover
      // opened. `null` (unreadable guest) falls through to the solo write this
      // button has always made.
      const probe =
        partyProbe === undefined
          ? await loadPartyProbe(eventId, value.guestId).catch(() => null)
          : partyProbe;
      if (probe && opensPartySheet(probe, can)) {
        setPartyProbe(probe);
        setOpen(false);
        setPartyOpen(true);
        return;
      }
      submitRef.current = true;
      setLoading(true);
      setOpen(false);
      // The fourth door onto this write, and the last one to get here. The
      // `GuestHandling` handler it used funnels every failure through
      // `handleErrorServer`, which keeps `message` and drops the body — and
      // this endpoint puts the reason for its 409 in `error`, with no
      // `errorCode` and no `message`. So `"isServerError" in result` was all
      // this could see, and a capacity block, a 403 and a 500 arrived as one
      // sentence. `runPartyCheckIn` calls the CLIENT and classifies.
      try {
        const report = await runPartyCheckIn(
          eventId,
          [value.guestId],
          shouldCheckIn,
          deviceName || "Admin Suite",
        );
        const result = report.results[0];
        if (!result || result.status === "failed") {
          showToast(
            checkInFailureText(
              result?.error,
              { checkIn: tCheckIn, errors: tErrors, party: tParty },
            ),
            "error",
          );
        } else {
          if (result.warning === "capacity") {
            // The write WENT THROUGH, over the limit. Same reasoning as the
            // refusal arm above: red here means "it failed".
            showToast(tCheckIn("capacityExceeded"), "info");
          }
          // The event refreshes the log rows and the count; it reaches nothing
          // that caches the guest list. `GuestsProvider` — which is what used
          // to invalidate those — is not mounted on this page.
          invalidateAfterCheckIn(queryClient, eventId);
          window.dispatchEvent(new Event("checkInActionCompleted"));
        }
      } catch {
        showToast(tPicker("checkInFailed"), "error");
      } finally {
        submitRef.current = false;
        setLoading(false);
      }
    };

    return (
      <>
        <button
          ref={buttonRef}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg body-3-medium transition-colors border outline-quaternary surface-primary font-primary hover:surface-tertiary"
          onClick={(e) => {
            e.stopPropagation();
            if (loading) return;
            setOpen(!open);
          }}
          disabled={loading}
        >
          {loading ? (
            <IconLoader2 size={14} className="animate-spin" />
          ) : shouldCheckIn ? (
            <IconQrcode size={14} />
          ) : (
            <IconQrcodeOff size={14} />
          )}
          <span className="whitespace-nowrap">
            {shouldCheckIn ? t("checkIn") : t("checkOut")}
          </span>
        </button>

        {open &&
          createPortal(
            <div
              ref={popoverRef}
              className="fixed z-50 surface-primary rounded-2xl border outline-quaternary shadow-xl p-4 flex flex-col gap-3 w-60"
              style={{ top: popoverPos.top, left: popoverPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <FormInput
                label={tPicker("deviceName")}
                border="outline-tertiary border focus-within:outline-focus"
                margin=""
                width="w-full"
                id="cta-device-name"
                height="h-9"
                value={deviceName}
                onChangeAction={setDeviceName}
                onEnterAction={handleConfirm}
              />
              <PrimaryButton
                size="small"
                width="w-full"
                text={shouldCheckIn ? t("checkIn") : t("checkOut")}
                onClick={handleConfirm}
                loading={loading}
              />
            </div>,
            document.body,
          )}

        {partyOpen && partyProbe && (
          <PCheckInParty
            eventId={eventId}
            row={partyProbe}
            // ONE direction for the party — the one this button offered.
            checkIn={shouldCheckIn}
            // The device name the operator typed above must survive the detour
            // through the sheet; it is what `performedByType` stamps on every
            // log row the run writes.
            performedByType={deviceName || "Admin Suite"}
            onClose={() => setPartyOpen(false)}
          />
        )}
      </>
    );
  },
);
